import React from "react";
import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";

/**
 * Warning shown on the ADM Design step when the user picks a design
 * whose format differs from the campaign's current `flyerType`
 * (typically set during the Segments step). Confirming the choice
 * propagates the new format into the campaign — which in turn
 * triggers a cost recalculation via `useCampaignCostPreviewCalculation`.
 *
 * Aesthetic, structure and copy mirror `DesignGalleryAddressBoxModal`
 * so the two warnings on this page read as a family: same Dialog
 * shell, same pink primary button, same outline secondary, same
 * typography.
 */

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialogContent-root, .MuiDialogTitle-root": {
      padding: 0,
    },
    // Override MUI's default Paper border-radius (4px) so the card
    // matches the buttons' 8px corners and the wider
    // 8px-design-system pieces in the campaign builder.
    "& .MuiDialog-paper": {
      borderRadius: "8px",
    },
  },
  content: {
    padding: "32px 24px 24px 24px",
    maxWidth: "600px",
  },
  title: {
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    marginBottom: "16px",
    "& > h2": {
      fontWeight: "700",
      fontSize: "22px",
      lineHeight: "26px",
      fontFamily: `"Inter", "Inter", "sans-serif"`,
      color: "#000",
    },
  },
  body: {
    textAlign: "center" as const,
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    fontFamily: `"Inter", "Inter", "sans-serif"`,
    margin: 0,
    color: "#555",
  },
  buttonContainer: {
    display: "flex",
    // Buttons hug the opposite edges of the content row so the
    // secondary/primary distinction is visually clear and there's
    // breathing room between them — matches the BrowserWarningModal
    // button layout.
    justifyContent: "space-between",
    width: "100%",
    marginTop: "32px",
  },
  primaryButton: {
    background: "#EE4360",
    border: "1px solid #EE4360",
    borderRadius: "8px",
    // Lock to a 40px-tall hit target. `padding: 0 24px` keeps the
    // horizontal breathing room; vertical padding is 0 because the
    // 40px height + flex centering already provides the right look.
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    padding: "0 24px",
    boxSizing: "border-box" as const,
    minWidth: "150px",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "20px",
    textTransform: "initial" as const,
    "&:hover": {
      background: "#FFFFFF",
      border: "1px solid #EE4360",
      color: "#EE4360",
    },
  },
  secondaryButton: {
    borderRadius: "8px",
    border: "1px solid #000",
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    padding: "0 24px",
    boxSizing: "border-box" as const,
    minWidth: "150px",
    fontWeight: "bold" as const,
    fontSize: "16px",
    lineHeight: "20px",
    textTransform: "initial" as const,
    background: "#FFFFFF",
    color: "#000",
    "&:hover": {
      background: "#F5F5F5",
    },
  },
});

interface DesignFormatChangeModalProps {
  open: boolean;
  /** Called when the user backs out — closes the modal and does NOT
   *  switch designs or change the campaign format. */
  onCancel: () => void;
  /** Called when the user confirms — caller should apply the pending
   *  design and let the format propagate to campaign state. */
  onConfirm: () => void;
  /** Total cost AFTER the format change (preview / dry-run). Rendered
   *  inline so the user knows exactly what they're committing to. */
  newTotal?: number;
  /** Current campaign total — used to compute the delta shown in
   *  parentheses next to `newTotal`. */
  currentTotal?: number;
  /** ISO 4217 currency code, e.g. "USD". Defaults to USD. */
  currency?: string;
}

const formatCurrency = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback if currency is invalid for some reason — keep at least
    // a readable number so the modal never breaks the flow.
    return `$${amount.toFixed(2)}`;
  }
};

export const DesignFormatChangeModal: React.FC<DesignFormatChangeModalProps> = ({
  open,
  onCancel,
  onConfirm,
  newTotal,
  currentTotal,
  currency = "USD",
}) => {
  const classes = useStyles();

  // Build the "$X.XX (+$Y.YY)" string only when both totals are
  // available and the delta is meaningful. Tiny rounding-noise deltas
  // (< 1 cent) are folded into "no change" so we don't show
  // distractingly precise zeroes.
  const hasCostInfo = typeof newTotal === "number" && typeof currentTotal === "number";
  const delta = hasCostInfo ? (newTotal as number) - (currentTotal as number) : 0;
  const deltaFmt = hasCostInfo
    ? Math.abs(delta) < 0.005
      ? "no change"
      : `${delta >= 0 ? "+" : "−"}${formatCurrency(Math.abs(delta), currency)}`
    : null;

  return (
    <Dialog
      open={open}
      // Backdrop click is a no-op — matches the AddressBox modal's
      // behavior. Users have to make an explicit choice between
      // "Update to new design" and "Cancel".
      onClose={() => {}}
      aria-labelledby="design-format-change-dialog"
      className={classes.dialog}
      data-cypress-id={"designFormatChangeModal"}
    >
      <Box className={classes.content} data-cypress-id={"designFormatChangeModalContentWrapper"}>
        <DialogContent data-cypress-id={"designFormatChangeModalContent"}>
          <DialogTitle
            id="design-format-change-dialog"
            className={classes.title}
            data-cypress-id={"designFormatChangeModalTitle"}
          >
            Update to new design?
          </DialogTitle>
          <DialogContentText className={classes.body} data-cypress-id={"designFormatChangeModalBody"}>
            {hasCostInfo ? (
              <>
                The design you selected has a different format than your campaign. Updating will change your campaign
                total to <strong>{formatCurrency(newTotal as number, currency)}</strong>{" "}
                <span data-cypress-id="designFormatChangeModalCostDelta">({deltaFmt})</span>.
              </>
            ) : (
              <>
                The design you selected has a different format than your campaign. Updating will change the campaign
                format and recalculate the campaign cost.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <Box className={classes.buttonContainer} data-cypress-id={"designFormatChangeModalButtonContainer"}>
          <Button
            className={classes.secondaryButton}
            onClick={onCancel}
            data-cypress-id={"designFormatChangeModalSecondaryButton"}
          >
            Cancel
          </Button>
          <Button
            className={classes.primaryButton}
            onClick={onConfirm}
            data-cypress-id={"designFormatChangeModalPrimaryButton"}
          >
            Update to new design
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
